import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Send, Loader2, CheckCircle2, Search, ShieldCheck, ClipboardList,
  PackageCheck, Truck, FileBadge2,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import { useI18n } from "@/i18n/I18nContext";
import { PAGES } from "@/i18n/pages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_ICONS = [Search, ShieldCheck, ClipboardList, PackageCheck, Truck, FileBadge2];

const HERO_IMG =
  "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?fm=jpg&q=80&w=2000&auto=format&fit=crop";

const ExportPage = () => {
  const { locale } = useI18n();
  const tr = PAGES[locale]?.export || PAGES.en.export;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company: "", country: "",
    product_category: "", product_details: "", quantity: "",
    target_market: "", timeline: "", budget_range: "", notes: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) er.full_name = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = true;
    if (!form.product_category.trim() || form.product_category.trim().length < 2) er.product_category = true;
    if (!form.product_details.trim() || form.product_details.trim().length < 5) er.product_details = true;
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        country: form.country.trim() || null,
        product_category: form.product_category.trim(),
        product_details: form.product_details.trim(),
        quantity: form.quantity.trim() || null,
        target_market: form.target_market.trim() || null,
        timeline: form.timeline.trim() || null,
        budget_range: form.budget_range.trim() || null,
        notes: form.notes.trim() || null,
        locale,
      };
      await axios.post(`${API}/export-request`, payload);
      setSubmitted(true);
      toast.success(tr.form.successTitle, { description: tr.form.successBody });
      setForm({
        full_name: "", email: "", phone: "", company: "", country: "",
        product_category: "", product_details: "", quantity: "",
        target_market: "", timeline: "", budget_range: "", notes: "",
      });
    } catch (err) {
      toast.error(tr.form.errorTitle, { description: tr.form.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="export-page">
      <Navbar />
      <main>
        <section className="relative overflow-hidden hero-wash">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
              <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-slate-500 hover:text-[hsl(var(--primary))]">
                <ArrowLeft className="h-3.5 w-3.5" /> Home
              </Link>
              <div className="mt-3 text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">{tr.eyebrow}</div>
              <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[hsl(var(--primary))]" data-testid="export-hero-title">
                {tr.hero.title}
              </h1>
              <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-slate-700">{tr.hero.subtitle}</p>
              <div className="mt-7">
                <Button asChild size="lg" className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white">
                  <a href="#export-form" data-testid="export-hero-cta">{tr.hero.cta}<Send className="ml-2 h-4 w-4" /></a>
                </Button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 -z-10 rounded-[28px] bg-[hsl(var(--accent))]/10 blur-2xl" />
                <div className="overflow-hidden rounded-3xl border border-[rgba(201,162,39,0.3)] aspect-[4/5]">
                  <img src={HERO_IMG} alt="Export & shipping" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 md:py-24 bg-[hsl(var(--tbs-cream-50))]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl text-[hsl(var(--primary))]">{tr.services.title}</h2>
              <p className="mt-3 text-slate-700">{tr.services.lead}</p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {tr.services.items.map((s, i) => {
                const Icon = SERVICE_ICONS[i] || PackageCheck;
                return (
                  <Card key={s.title} data-testid={`export-service-${i}`} className="card-lift bg-white border border-slate-200 rounded-2xl p-6">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 font-display text-xl text-[hsl(var(--primary))]">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section id="export-form" className="py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
                {tr.eyebrow}
              </div>
              <h2 className="mt-3 font-display text-3xl md:text-4xl text-[hsl(var(--primary))]">{tr.form.title}</h2>
              <p className="mt-3 text-slate-700">{tr.form.lead}</p>
            </div>

            <Card className="mt-10 bg-white border border-slate-200 rounded-3xl p-6 md:p-8">
              <form onSubmit={onSubmit} data-testid="export-request-form" className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={tr.form.labels.fullName} required error={errors.full_name}>
                    <Input value={form.full_name} onChange={onChange("full_name")} className="h-11" data-testid="export-full-name" required />
                  </Field>
                  <Field label={tr.form.labels.email} required error={errors.email}>
                    <Input type="email" value={form.email} onChange={onChange("email")} className="h-11" data-testid="export-email" required />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label={tr.form.labels.phone}>
                    <Input value={form.phone} onChange={onChange("phone")} className="h-11" data-testid="export-phone" />
                  </Field>
                  <Field label={tr.form.labels.company}>
                    <Input value={form.company} onChange={onChange("company")} className="h-11" data-testid="export-company" />
                  </Field>
                  <Field label={tr.form.labels.country}>
                    <Input value={form.country} onChange={onChange("country")} className="h-11" data-testid="export-country" />
                  </Field>
                </div>

                <Field label={tr.form.labels.productCategory} required error={errors.product_category}>
                  <Input value={form.product_category} onChange={onChange("product_category")} placeholder={tr.form.placeholders.productCategory} className="h-11" data-testid="export-product-category" required />
                </Field>
                <Field label={tr.form.labels.productDetails} required error={errors.product_details}>
                  <Textarea value={form.product_details} onChange={onChange("product_details")} placeholder={tr.form.placeholders.productDetails} rows={4} data-testid="export-product-details" required />
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={tr.form.labels.quantity}>
                    <Input value={form.quantity} onChange={onChange("quantity")} placeholder={tr.form.placeholders.quantity} className="h-11" data-testid="export-quantity" />
                  </Field>
                  <Field label={tr.form.labels.targetMarket}>
                    <Input value={form.target_market} onChange={onChange("target_market")} className="h-11" data-testid="export-target-market" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={tr.form.labels.timeline}>
                    <Input value={form.timeline} onChange={onChange("timeline")} placeholder={tr.form.placeholders.timeline} className="h-11" data-testid="export-timeline" />
                  </Field>
                  <Field label={tr.form.labels.budgetRange}>
                    <Input value={form.budget_range} onChange={onChange("budget_range")} placeholder={tr.form.placeholders.budgetRange} className="h-11" data-testid="export-budget" />
                  </Field>
                </div>
                <Field label={tr.form.labels.notes}>
                  <Textarea value={form.notes} onChange={onChange("notes")} rows={3} data-testid="export-notes" />
                </Field>

                <div className="flex items-center justify-between gap-4 pt-2">
                  {submitted ? (
                    <div data-testid="export-success" className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" />
                      {tr.form.successTitle}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">{tr.form.confidential}</span>
                  )}
                  <Button type="submit" size="lg" disabled={submitting} data-testid="export-submit" className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white">
                    {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{tr.form.submitting}</>) : (<>{tr.form.submit}<Send className="ml-2 h-4 w-4" /></>)}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

const Field = ({ label, required, error, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
      {label} {required && <span className="text-[hsl(var(--accent))]">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {error && <div className="mt-1 text-xs text-red-600">Required</div>}
  </div>
);

export default ExportPage;
